// src/pages/Reports/DepositAccount/DepositAccountLedgerReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
} from "antd";
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";
import moment from "moment";

import ReportDom from "../../../components/common/ReportDom";
import {
  currentDateStartTime,
  currentDateEndTime,
  dateFormat,
} from "../../../lib/functions";
import { getDepositAccountLedgerApi } from "../../../apiservice/reports";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import "../../../components/common/bt.css";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const DepositAccountLedgerReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // -------------------------------
  // Filters
  // -------------------------------
  const [filters, setFilters] = useState({
    account: null,
    fromDate: currentDateStartTime(dayjs()),
    toDate: currentDateEndTime(dayjs()),
  });

  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accLoading, setAccLoading] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // -------------------------------
  // Quick Range
  // -------------------------------
  const setQuickRange = (range) => {
    const today = dayjs();
    let fromDate, toDate;

    switch (range) {
      case "last7days":
        fromDate = currentDateStartTime(today.subtract(6, "day"));
        toDate = currentDateEndTime(today);
        break;
      case "thisMonth":
        fromDate = currentDateStartTime(today.startOf("month"));
        toDate = currentDateEndTime(today.endOf("month"));
        break;
      default:
        fromDate = currentDateStartTime(today);
        toDate = currentDateEndTime(today);
        break;
    }

    handleFilterChange("fromDate", fromDate);
    handleFilterChange("toDate", toDate);
  };

  // -------------------------------
  // Fetch Accounts
  // -------------------------------
  const fetchAccounts = async (query) => {
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        multiType: ["deposit_accounts"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setAccLoading(false);
    }
  };

  // -------------------------------
  // Fetch Report
  // -------------------------------
  const fetchReport = async () => {
    if (!filters.account) {
      message.warning("Please select an account");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        accId: filters.account?.acc_id ?? null,
        dateFrom: filters.fromDate,
        dateTo: filters.toDate,
      };

      const res = await getDepositAccountLedgerApi(payload);

      setOpeningBalance(res.data.opening_balance || 0);
      setData(res.data.ledger || []);
      setClosingBalance(res.data.closing_balance || 0);
    } catch (err) {
      console.error(err);
      message.error("Error fetching Deposit Account Ledger Report");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // Print
  // -------------------------------
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Deposit Account Ledger",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  // -------------------------------
  // Columns (Matches Old UI Exactly)
  // -------------------------------
  const columns = [
    { title: "No.", render: (_, __, i) => i + 1, width: 60 },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (v) => moment(v).format(dateFormat),
      width: 120,
    },
    { title: "Particular", dataIndex: "particular", width: 200 },
    { title: "VCH No", dataIndex: "vch_no", width: 100 },
    { title: "VCH Type", dataIndex: "vch_type", width: 100 },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      align: "right",
      width: 120,
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      align: "right",
      width: 120,
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      width: 120,
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  // -------------------------------
  // Table Data (Old UI Structure Match)
  // -------------------------------
  const tableData = [
    {
      key: "opening",
      rowType: "opening",
      particular: "Opening Balance :",
      debit_amount: openingBalance,
      credit_amount: null,
      balance: null,
    },
    ...data.map((row, i) => ({
      ...row,
      key: `row-${i}`,
      rowType: "ledger",
    })),
    {
      key: "grandtotal",
      rowType: "grand",
      particular: "Grand Total :",
      debit_amount: _.sumBy(data, (r) => Number(r.debit_amount || 0)),
      credit_amount: _.sumBy(data, (r) => Number(r.credit_amount || 0)),
      balance: null,
    },
    {
      key: "closing",
      rowType: "closing",
      particular: "Closing Balance :",
      debit_amount: null,
      credit_amount: closingBalance,
      balance: null,
    },
  ];

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle" >
              Deposit Account Ledger
            </Title>
          </div>
        }
        extra={
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            { !screens.xs && "Print" }
          </Button>
        }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
        <Row gutter={[16, 16]}>
          {/* Search Account */}
          <Col xs={24} sm={12} md={6}>
            <label>Select Account</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={accounts.map((a) => ({
                label: `${a.acc_name} - ${a.acc_code || ""}`,
                value: `${a.acc_name} - ${a.acc_code || ""}`,
                account: a,
              }))}
              onSelect={(_, option) => handleFilterChange("account", option.account)}
              onSearch={fetchAccounts}
              placeholder="Search Account"
              loading={accLoading}
              allowClear
            >
              <Input />
            </AutoComplete>
          </Col>

          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />

          {/* Report Button */}
          <Col xs={24} sm={12} md={4} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchReport}
              loading={loading}
              style={{height: 35.19}}
              block
            >
              Report
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey="key"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          loading={loading}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          // Remove extra empty rows
          locale={{ emptyText: "" }}
        />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Deposit Account Ledger"
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <table className="ledger-print-table">
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "29%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <tbody>
                <tr className="ledger-print-heading">
                  <td>No.</td>
                  <td>Date</td>
                  <td>Particular</td>
                  <td>VCH No</td>
                  <td>VCH Type</td>
                  <td style={{ textAlign: "right" }}>Debit</td>
                  <td style={{ textAlign: "right" }}>Credit</td>
                  <td style={{ textAlign: "right" }}>Balance</td>
                </tr>

                <tr className="ledger-opening-row">
                  <td colSpan={7} style={{ textAlign: "right" }}>
                    Opening Balance
                  </td>
                  <td style={{ textAlign: "right" }}>{format(Number(openingBalance || 0).toFixed(2))}</td>
                </tr>

                {data.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{index + 1}</td>
                    <td>{row.created_date ? moment(row.created_date).format(dateFormat) : "-"}</td>
                    <td>{row.particular}</td>
                    <td>{row.vch_no}</td>
                    <td>{row.vch_type}</td>
                    <td style={{ textAlign: "right" }}>{format(Number(row.debit_amount || 0).toFixed(2))}</td>
                    <td style={{ textAlign: "right" }}>{format(Number(row.credit_amount || 0).toFixed(2))}</td>
                    <td style={{ textAlign: "right" }}>{format(Number(row.balance || 0).toFixed(2))}</td>
                  </tr>
                ))}

                <tr className="ledger-total-row">
                  <td colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
                  <td style={{ textAlign: "right" }}>
                    {format(_.sumBy(data, (r) => Number(r.debit_amount || 0)).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(_.sumBy(data, (r) => Number(r.credit_amount || 0)).toFixed(2))}
                  </td>
                  <td />
                </tr>

                <tr className="ledger-closing-row">
                  <td colSpan={7} style={{ textAlign: "right" }}>
                    Closing Balance
                  </td>
                  <td style={{ textAlign: "right" }}>{format(Number(closingBalance || 0).toFixed(2))}</td>
                </tr>
              </tbody>
            </table>
          )}
        />
      </div>
    </div>
  );
};

export default DepositAccountLedgerReport;
